import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import OptimizedVideo from '../components/OptimizedVideo';

interface HeroProps {
  leftTitle?: string;
  leftDesc?: string;
  rightTitle?: string;
  rightDesc?: string;
  showButtons?: boolean;
  isCustomContent?: boolean;
  children?: React.ReactNode;
}

export default function Hero({ 
  leftTitle, 
  leftDesc, 
  rightTitle, 
  rightDesc, 
  showButtons = true,
  isCustomContent = false,
  children
}: HeroProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { language } = useLanguage();
  
  const defaultLeftTitle = language === 'PL' ? 'Solid Agency© - Agencja marketingowa / Lublin' : 'Solid Agency© - Marketing Agency / Lublin';
  const defaultLeftDesc = language === 'PL' 
    ? 'Skupiamy się na dostarczaniu wysokowydajnych rozwiązań cyfrowych, które realnie skalują Twój biznes.'
    : 'We focus on delivering high-performance digital solutions that effectively scale your business.';
  
  const defaultRightTitle = language === 'PL' ? 'Budujemy strony internetowe, które ładują się natychmiast.' : 'We build websites that load instantly.';
  const defaultRightDesc = language === 'PL'
    ? 'Pozbywamy się zbędnego kodu, aby Twoi klienci nie tracili czasu na czekanie, a Ty - na tracenie szans sprzedażowych.'
    : 'We eliminate unnecessary code so your clients don\'t waste time waiting, and you don\'t lose sales opportunities.';
  
  return (
    <section ref={containerRef} className="sticky top-0 w-full h-screen overflow-hidden text-white bg-black z-0">
      <div className="absolute inset-0 w-full h-full opacity-60">
        {/* BACKGROUND VIDEO & POSTER */}
        <OptimizedVideo 
          className="absolute inset-0 w-full h-full"
          poster="/PosterBackgroundPC.webp"
          posterMobile="/PosterBackgroundMOBILE.webp"
          sources={[
            { src: "/BackgroundPC.mp4", media: "(min-width: 801px)" },
            { src: "/BackgroundMOBILE.mp4", media: "(max-width: 800px)" }
          ]}
          preload="auto" // Critical for first fold LCP
          eager={true}
        />
      </div>

      {/* CONTENT LAYER */}
      <div 
        className="relative z-10 w-full h-full flex flex-col px-6 sm:px-16 transition-transform duration-75 justify-start pt-6 sm:pt-20"
      >
        
        {/* Main Info Row */}
        {children ? (
            <div className="flex flex-col w-full h-full justify-center">
              {children}
            </div>
        ) : isCustomContent ? (
          <div className="flex flex-col w-full h-full">
            <div className="flex-1 flex flex-col justify-end pb-4 sm:pb-6">
              <div className="max-w-[1200px] mx-auto w-full">
                <div className="flex justify-start">
                  <div className="max-w-[900px]">
                    <h1 className="text-xl sm:text-3xl md:text-4xl font-bold leading-tight uppercase">
                      {leftTitle}
                    </h1>
                  </div>
                </div>
              </div>
            </div>

            {/* DIVIDER */}
            <div className="max-w-[1200px] mx-auto w-full">
              <div className="w-full h-[1px] bg-white/30"></div>
            </div>

            <div className="flex-1 flex flex-col justify-start pt-4 sm:pt-6">
               <div className="max-w-[1200px] mx-auto w-full">
                <div className="flex justify-end">
                  <div className="max-w-[650px] text-left sm:text-right">
                    <h2 className="text-[16px] font-inter-display font-normal leading-[1.2] tracking-[-0.04em] text-white/80">
                      {rightDesc}
                    </h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col">
             {/* TOP ROW */}
             <div className="flex-1 flex flex-col justify-end pb-4 sm:pb-6">
               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end w-full gap-4">
                 <div className="text-left w-full sm:max-w-[550px] md:max-w-[800px]">
                   <h1 className="font-syne font-bold font-[700] text-[20px] sm:text-[28px] md:text-[38px] lg:text-[44px] leading-tight block">
                     {defaultLeftTitle}
                   </h1>
                 </div>
                 <div className="text-left sm:text-right w-full sm:max-w-[450px] md:max-w-[450px]">
                   <span className="text-[13px] md:text-[16px] font-inter-display font-medium leading-[1.5] tracking-[-0.01em] text-white/80 block sm:ml-auto w-full sm:w-[85%]">
                     {defaultLeftDesc}
                   </span>
                 </div>
               </div>
             </div>

             {/* DIVIDER */}
             <div className="w-full h-[1px] bg-white/20"></div>

             {/* BOTTOM ROW - with buttons pushed to follow text */}
             <div className="flex-1 flex flex-col justify-start pt-4 sm:pt-6">
               <div className="flex flex-col sm:flex-row justify-between items-start w-full gap-4 sm:gap-12 md:gap-20 mb-8 sm:mb-12">
                 <div className="text-left w-full sm:max-w-[450px] md:max-w-[450px] order-last sm:order-first">
                   <span className="text-[13px] md:text-[16px] font-inter-display font-medium leading-[1.5] tracking-[-0.01em] text-white/80 block w-full sm:w-[85%]">
                     {defaultRightDesc}
                   </span>
                 </div>
                 <div className="text-left sm:text-right w-full sm:max-w-[450px] md:max-w-[550px] lg:max-w-[600px] order-first sm:order-last">
                   <h2 className="font-syne font-bold font-[700] text-[19px] sm:text-[28px] md:text-[32px] leading-tight block text-right sm:text-left md:text-right">
                     {defaultRightTitle}
                   </h2>
                 </div>
               </div>

               {showButtons && (
                <div className="flex justify-end sm:justify-center w-full mt-4 sm:mt-0">
                  <div className="flex flex-row items-center justify-end sm:justify-center gap-2 sm:gap-4 font-inter-display pointer-events-auto">
                    <Link to="/zacznij-projekt" className="bg-white text-zinc-900 px-5 sm:px-8 py-2.5 sm:py-4 rounded-full font-medium text-[12px] sm:text-base transition-transform hover:scale-105 whitespace-nowrap">
                      {language === 'PL' ? 'Darmowy audyt' : 'Free Audit'}
                    </Link>
                    <Link to="/oferta" className="bg-transparent border border-white/20 text-white px-5 sm:px-8 py-2.5 sm:py-4 rounded-full font-medium text-[12px] sm:text-base transition-transform hover:scale-105 backdrop-blur-md whitespace-nowrap">
                      {language === 'PL' ? 'Oferta' : 'Offer'}
                    </Link>
                  </div>
                </div>
               )}
             </div>
          </div>
        )}
      </div>
    </section>
  );
}
